import { useCallback, useEffect, useState } from 'react';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';

export interface RateLimitResource {
  limit: number;
  used: number;
  remaining: number;
  reset: number;
}

export interface RateLimitResponse {
  resources: Record<string, RateLimitResource>;
  rate: RateLimitResource;
}

export interface UseGitHubRateLimitResult {
  data: RateLimitResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Fetches the GitHub App installation token rate-limit status from the
 * homestead backend on mount, and exposes a `refresh()` to re-fetch on demand.
 *
 * Kept separate from the presentational view so that the network/state plumbing
 * can be tested in isolation via `renderHook`, and the view can be tested with
 * synthetic props without needing a Backstage `TestApiProvider` or fetch mock.
 */
export const useGitHubRateLimit = (): UseGitHubRateLimitResult => {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const [data, setData] = useState<RateLimitResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const backendUrl = configApi.getString('backend.baseUrl');

  const fetchRateLimit = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { token } = await identityApi.getCredentials();
      const response = await fetch(`${backendUrl}/api/github-rate-limit/status`, {
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(
          body.error ?? `Request failed with status ${response.status}`,
        );
      }

      const json: RateLimitResponse = await response.json();
      setData(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [backendUrl, identityApi]);

  useEffect(() => {
    fetchRateLimit();
  }, [fetchRateLimit]);

  return { data, loading, error, refresh: fetchRateLimit };
};
